import { Button, Modal, Spinner, Tooltip } from '@shopify/polaris';
import { useEffect, useMemo, useState } from 'react';
import { useLookupStore } from '../../context/useStore.js';
import { doc_spec, getSharePointPath, languageMap } from '../../utils/sharepoint.js';

export function getSystemDocumentFileName(docType, itemNumber, languageId) {
  const languageKey = languageMap[languageId] || 'N';
  const documentTypeSpec = doc_spec[docType];
  if (!documentTypeSpec?.lan?.[languageKey]) return null;
  return `${documentTypeSpec.lan[languageKey]} ${itemNumber}.pdf`;
}

export default function SharePointPdfPreviewButton({
  companyNumber,
  documentTypeId,
  itemNumber,
  fileName,
  systemDocType,
  languageId,
  buttonLabel = 'PDF',
  tooltipContent,
  modalTitle = 'PDF preview'
}) {
  const documenttypes = useLookupStore((state) => state.documenttypes);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pdfLink, setPdfLink] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const resolvedFileName = useMemo(() => fileName || getSystemDocumentFileName(systemDocType, itemNumber, languageId), [fileName, systemDocType, itemNumber, languageId]);
  const canPreview = Boolean(companyNumber && documentTypeId && itemNumber && resolvedFileName && documenttypes?.length);

  useEffect(() => {
    return () => {
      if (pdfLink) URL.revokeObjectURL(pdfLink);
    };
  }, [pdfLink]);

  const handleClose = () => {
    setOpen(false);
    setErrorMessage('');
    setPdfLink((currentLink) => {
      if (currentLink) URL.revokeObjectURL(currentLink);
      return null;
    });
  };

  const handleOpen = async () => {
    if (!canPreview) return;
    setOpen(true);
    setLoading(true);
    setErrorMessage('');
    setPdfLink((currentLink) => {
      if (currentLink) URL.revokeObjectURL(currentLink);
      return null;
    });

    try {
      const sharepointPath = getSharePointPath(companyNumber, documentTypeId, itemNumber, documenttypes);
      const response = await fetch('/api/docgen/streamsharepointdoc', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: sharepointPath, fileName: resolvedFileName })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.msg || 'The PDF preview could not be loaded.');
      }

      const blob = await response.blob();
      if (blob.type !== 'application/pdf') {
        throw new Error('The requested file is not a PDF.');
      }

      setPdfLink(URL.createObjectURL(blob));
    } catch (error) {
      setErrorMessage(error.message || 'The PDF preview could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  if (!canPreview) return '';

  const button = (
    <Button size="slim" variant="plain" onClick={() => void handleOpen()}>
      {buttonLabel}
    </Button>
  );

  return (
    <>
      {tooltipContent ? <Tooltip content={tooltipContent}>{button}</Tooltip> : button}
      <Modal open={open} onClose={handleClose} title={modalTitle} large>
        <Modal.Section>
          <div style={{ width: 'min(96vw, 1800px)', height: '88vh' }}>
            {loading ? (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spinner accessibilityLabel="Loading PDF preview" size="large" />
              </div>
            ) : errorMessage ? (
              <div style={{ padding: '16px', color: '#8e1f0b' }}>{errorMessage}</div>
            ) : pdfLink ? (
              <iframe title={modalTitle} src={pdfLink} style={{ width: '100%', height: '100%', border: 'none' }} />
            ) : null}
          </div>
        </Modal.Section>
      </Modal>
      {open ? (
        <style>{`
          .Polaris-Modal-Dialog__Modal {
            width: min(96vw, 1800px) !important;
            max-width: 96vw !important;
          }
        `}</style>
      ) : null}
    </>
  );
}
